import { Slot } from 'radix-ui';
import * as React from 'react';
import type { VariantProps } from 'tailwind-variants';

import { tv } from '../../lib/tv';

/**
 * shadcn Card, restyled to the Figma Card (page ↳ Well & Card, 52:215):
 * bg/surface, radius/lg, clipped. Outlined = 1px border/subtle (Hover
 * border/strong); Elevated = Shadow/SM (Hover Shadow/LG); Selected = 2px
 * brand/strong border on either. Strokes are inside and included in layout
 * in Figma, i.e. a CSS border. Parts: Media (180px, Play + Duration),
 * Content (16px, gap 6: Eyebrow, Title, Description), Footer (16px sides and
 * bottom, gap 8). Apps use the wrappers in components/lib/card.
 */
export const cardVariants = tv({
  slots: {
    root: [
      'bg-bg-surface relative flex flex-col overflow-hidden rounded-lg text-left outline-none',
      // Motion: border and shadow 120ms ease-out, like the controls.
      'transition-[border-color,box-shadow] duration-[120ms] ease-out',
    ],
    media: [
      'relative flex h-45 shrink-0 items-center justify-center overflow-hidden',
      // Figma placeholder fill: olive/950 → olive/900 at 157°.
      'bg-[linear-gradient(157deg,var(--color-olive-950)_7%,var(--color-olive-900)_79%)]',
      '[&>img,&>video]:absolute [&>img,&>video]:inset-0 [&>img,&>video]:size-full [&>img,&>video]:object-cover',
    ],
    play: 'bg-brand-primary text-icon-primary relative flex size-11 items-center justify-center rounded-full',
    playIcon: 'size-5',
    duration:
      'bg-bg-overlay text-label-sm text-text-inverse absolute right-3 bottom-3 rounded-xs px-1.5 py-0.5',
    content: 'flex flex-col gap-1.5 p-4',
    eyebrow: 'text-overline text-text-tertiary uppercase',
    title: 'text-heading-h4 text-text-primary',
    description: 'text-body-sm text-text-secondary',
    footer: 'flex items-center gap-2 px-4 pb-4',
    meta: 'text-caption text-text-secondary min-w-0 flex-1',
  },
  variants: {
    variant: {
      outlined: { root: 'border-border-subtle border' },
      elevated: { root: 'shadow-sm' },
    },
    selected: {
      true: { root: 'border-brand-strong border-2' },
    },
    interactive: {
      true: { root: 'focus-visible:shadow-focus-ring cursor-pointer' },
    },
  },
  compoundVariants: [
    {
      variant: 'outlined',
      selected: false,
      interactive: true,
      class: { root: 'hover:border-border-strong' },
    },
    {
      variant: 'elevated',
      interactive: true,
      class: { root: 'hover:shadow-lg' },
    },
  ],
  defaultVariants: { variant: 'outlined', selected: false, interactive: false },
});

type CardVariants = VariantProps<typeof cardVariants>;

function Card({
  className,
  variant,
  selected,
  interactive,
  asChild = false,
  ...props
}: React.ComponentProps<'div'> & CardVariants & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : 'div';
  return (
    <Comp
      data-slot="card"
      data-selected={selected || undefined}
      className={cardVariants({ variant, selected, interactive }).root({ className })}
      {...props}
    />
  );
}

const part =
  <K extends 'media' | 'content' | 'eyebrow' | 'description' | 'footer' | 'meta'>(
    slot: K,
    name: string,
  ) =>
  ({ className, ...props }: React.ComponentProps<'div'>) => (
    <div data-slot={name} className={cardVariants()[slot]({ className })} {...props} />
  );

const CardMedia = part('media', 'card-media');
const CardContent = part('content', 'card-content');
const CardEyebrow = part('eyebrow', 'card-eyebrow');
const CardDescription = part('description', 'card-description');
const CardFooter = part('footer', 'card-footer');
const CardMeta = part('meta', 'card-meta');

function CardTitle({
  className,
  asChild = false,
  ...props
}: React.ComponentProps<'h3'> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : 'h3';
  return (
    <Comp
      data-slot="card-title"
      className={cardVariants().title({ className })}
      {...props}
    />
  );
}

export {
  Card,
  CardContent,
  CardDescription,
  CardEyebrow,
  CardFooter,
  CardMedia,
  CardMeta,
  CardTitle,
};
